import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  Truck,
  Building2,
  Calendar,
  Eye,
  DollarSign,
  Package,
  CheckCircle2,
  Clock,
  MapPin,
  RefreshCw } from
'lucide-react';

const API_URL = API_BASE || (
  typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname)
    ? 'http://localhost:8001'
    : ''
);

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const statusColors = {
  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400',
  processing: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400',
  shipped: 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-400',
  delivered: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400',
  cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-400'
};

export const DropshipOrders = () => {const { t } = useTranslation("crm");
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewDropship, setShowNewDropship] = useState(false);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/crm/dropship-orders`;
      if (activeTab !== 'all') {
        url += `?status=${activeTab}`;
      }
      const response = await fetch(url, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch dropship orders');
      const data = await response.json();
      setOrders(data);
    } catch (err) {
      toast.error('Failed to load dropship orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [activeTab]);

  const filteredOrders = orders.filter((order) => {
    if (!searchTerm) return true;
    return order.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.customer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.vendor?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const stats = {
    total: orders.length,
    pending: orders.filter((o) => o.status === 'pending' || o.status === 'processing').length,
    shipped: orders.filter((o) => o.status === 'shipped').length,
    delivered: orders.filter((o) => o.status === 'delivered').length,
    totalValue: orders.reduce((sum, o) => sum + (o.order_value || 0), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="crm-dropship-orders">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("dropshipOrders", "Dropship Orders")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("manageDropshipFulfillment", "Manage dropship fulfillment and vendor shipments")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchOrders} className="gap-2">
            <RefreshCw className="h-4 w-4" />{t("refresh", "Refresh")}

          </Button>
          <Button className="gap-2 bg-teal-600 hover:bg-teal-700" onClick={() => setShowNewDropship(true)}>
            <Plus className="h-4 w-4" />{t("newDropship", "New Dropship")}

          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-5">
        <Card className="card-elevated border-l-4 border-l-teal-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-teal-500/10">
                <Truck className="h-5 w-5 text-teal-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("totalOrders", "Total Orders")}</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("pending", "Pending")}</p>
                <p className="text-2xl font-bold">{stats.pending}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-purple-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <Package className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("shipped", "Shipped")}</p>
                <p className="text-2xl font-bold">{stats.shipped}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("delivered", "Delivered")}</p>
                <p className="text-2xl font-bold">{stats.delivered}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-orange-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-500/10">
                <DollarSign className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t("totalValue", "Total Value")}</p>
                <p className="text-2xl font-bold">${(stats.totalValue / 1000).toFixed(0)}k</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Table */}
      <Card className="card-elevated">
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by order ID, customer, or vendor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10" />
              
            </div>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="all">{t("all", "All")}</TabsTrigger>
                <TabsTrigger value="pending">{t("pending", "Pending")}</TabsTrigger>
                <TabsTrigger value="processing">{t("processing", "Processing")}</TabsTrigger>
                <TabsTrigger value="shipped">{t("shipped", "Shipped")}</TabsTrigger>
                <TabsTrigger value="delivered">{t("delivered", "Delivered")}</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("orderId", "Order ID")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("customer", "Customer")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("vendor", "Vendor")}</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("status", "Status")}</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">{t("value", "Value")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("tracking", "Tracking")}</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("created", "Created")}</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">{t("actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map((order) =>
                <tr key={order.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Truck className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{order.id}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <span>{order.customer}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-muted-foreground">{order.vendor}</span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <Badge className={statusColors[order.status] || statusColors.pending}>
                        {order.status === 'delivered' && <CheckCircle2 className="h-3 w-3 mr-1" />}
                        {order.status === 'shipped' && <Truck className="h-3 w-3 mr-1" />}
                        {order.status}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="font-semibold text-teal-600">${order.order_value?.toLocaleString() || 0}</span>
                    </td>
                    <td className="py-3 px-4">
                      {order.tracking ?
                    <div className="flex items-center gap-1.5 text-sm">
                          <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="text-teal-600 font-mono text-xs">{order.tracking}</span>
                        </div> :

                    <span className="text-muted-foreground">-</span>
                    }
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                        {new Date(order.created_at).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <Eye className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {filteredOrders.length === 0 &&
          <div className="py-12 text-center">
              <Truck className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t("noDropshipOrders", "No dropship orders found")}</h3>
              <p className="text-muted-foreground">{t("tryAdjustingYour", "Try adjusting your search or filter criteria")}</p>
            </div>
          }
        </CardContent>
      </Card>
      {showNewDropship && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div role="dialog" aria-modal="true" aria-label="New Dropship" className="w-full max-w-2xl rounded-lg border bg-background shadow-xl">
            <div className="border-b p-5">
              <h2 className="text-lg font-semibold">New Dropship</h2>
              <p className="text-sm text-muted-foreground">Create a dropship order with customer, vendor, value, status, and tracking.</p>
            </div>
            <div className="grid gap-4 p-5 sm:grid-cols-2">
              <Input placeholder="Customer" />
              <Input placeholder="Vendor" />
              <Input placeholder="Order value" />
              <Input placeholder="Status" defaultValue="pending" />
              <Input placeholder="Tracking number" />
              <Input placeholder="Requested ship date" />
            </div>
            <div className="flex justify-end gap-2 border-t p-5">
              <Button variant="outline" onClick={() => setShowNewDropship(false)}>Cancel</Button>
              <Button className="bg-teal-600 hover:bg-teal-700">Create Dropship</Button>
            </div>
          </div>
        </div>
      )}
    </div>);

};
